/**
 * KOPS-662: Brokerage Deal Board — Kanban for TC + Broker workflow
 *
 * Brokerage-side view of all agent transactions. TC reviews paperwork,
 * broker approves. Cards move through: New File → In Progress → Under Contract
 * → Broker Review → Closed & Filed.
 *
 * This is the "broker controls the process" side of the operating model.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolderOpen, faSpinner as faSpinnerIcon, faHome, faHandshake,
  faCheckCircle, faTrophy, faUser, faSearch, faSyncAlt,
  faFileAlt, faExclamationTriangle, faShieldAlt, faGripVertical,
  faDollarSign, faMapMarkerAlt, faClock, faCheck, faTimes,
  faMoneyBillWave, faSearchDollar, faUniversity, faGavel,
  faPaperPlane, faClipboardCheck, faArrowRight, faSignHanging,
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';
import toast from 'react-hot-toast';

// ---------------------------------------------------------------------------
// Colors (navy + gold — matching agent deal board)
// ---------------------------------------------------------------------------
const C = {
  navy: '#0F1E35', navy2: '#162844', navy3: '#1E3557',
  gold: '#C9952A', gold2: '#E8B44A', gold3: '#F5D080',
  white: '#FFFFFF', off: '#E8EEF6', muted: '#8FA3BF',
  green: '#27ae60', red: '#e74c3c', orange: '#f39c12', blue: '#3498db',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface BrokerageDealCard {
  id: string;
  type: string;
  stage: string;
  dealId: string | null;
  clientName: string;
  agentName: string;
  agentEmail: string;
  agentPhoto: string | null;
  agentId: string;
  propertyAddress: string;
  propertyCity: string;
  propertyState: string;
  propertyPrice: number | null;
  complianceStatus: string;
  complianceScore: number;
  documentReviewStatus: string;
  docCount: number;
  brokerApprovedAt: string | null;
  brokerApprovedBy: string | null;
  daysInStage: number;
  notes: string;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
}

interface StageConfig {
  key: string;
  label: string;
  color: string;
  icon: any;
}

// ---------------------------------------------------------------------------
// Brokerage processing stages
// ---------------------------------------------------------------------------
const STAGES: StageConfig[] = [
  { key: 'listing',           label: 'Listing',                 color: '#16a085', icon: faSignHanging },
  { key: 'pre_contract',      label: 'New File',                color: '#e74c3c', icon: faFolderOpen },
  { key: 'showing',           label: 'Showing',                 color: '#e67e22', icon: faHome },
  { key: 'earnest_money',     label: 'Earnest Money',           color: '#d35400', icon: faMoneyBillWave },
  { key: 'under_contract',    label: 'Under Contract',          color: '#f39c12', icon: faHandshake },
  { key: 'inspection',        label: 'Inspection Period',       color: '#f1c40f', icon: faSearchDollar },
  { key: 'appraisal',         label: 'Appraisal',              color: '#3498db', icon: faHome },
  { key: 'loan_approval',     label: 'Loan Approval',           color: '#2980b9', icon: faUniversity },
  { key: 'closing',           label: 'Closing',                 color: '#8e44ad', icon: faGavel },
  { key: 'broker_review',     label: 'Broker Sign-off',         color: '#9b59b6', icon: faShieldAlt },
  { key: 'completed',         label: 'Closed & Filed',          color: '#27ae60', icon: faTrophy },
];

// ---------------------------------------------------------------------------
// Compliance badge
// ---------------------------------------------------------------------------
const ComplianceBadge: React.FC<{ status: string }> = ({ status }) => {
  const config: Record<string, { bg: string; text: string; label: string }> = {
    compliant: { bg: '#27ae60', text: '#fff', label: 'Compliant' },
    pending:   { bg: '#f39c12', text: '#fff', label: 'Pending' },
    warning:   { bg: '#e67e22', text: '#fff', label: 'Warning' },
    critical:  { bg: '#e74c3c', text: '#fff', label: 'Critical' },
  };
  const c = config[status] || config.pending;
  return (
    <span style={{
      background: c.bg, color: c.text, padding: '2px 8px', borderRadius: 4,
      fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
    }}>{c.label}</span>
  );
};

// ---------------------------------------------------------------------------
// Type badge
// ---------------------------------------------------------------------------
const TypeBadge: React.FC<{ type: string }> = ({ type }) => {
  const isListing = type === 'listing';
  return (
    <span style={{
      background: isListing ? '#27ae60' : '#3498db',
      color: '#fff', padding: '2px 8px', borderRadius: 4,
      fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
    }}>{isListing ? 'Listing' : 'Buyer'}</span>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
// Required docs by stage — what TC checks at each phase
const STAGE_REQUIRED_DOCS: Record<string, string[]> = {
  listing: ['Listing Agreement', 'Agency Disclosure', 'Seller Disclosure (SPDS)'],
  pre_contract: ['Employment/Listing Agreement', 'Pre-Approval Letter', 'Agency Disclosure'],
  showing: ['Buyer-Broker Agreement', 'Agency Disclosure', 'Pre-Approval Letter'],
  earnest_money: ['Purchase Contract', 'Earnest Money Receipt', 'Escrow Instructions'],
  under_contract: ['Purchase Contract', 'Earnest Money Receipt', 'Agency Disclosure'],
  inspection: ['Inspection Report', 'BINSR (Buyer Inspection Notice)', 'Repair Addendum'],
  appraisal: ['Appraisal Report', 'Appraisal Contingency Waiver'],
  loan_approval: ['Loan Approval Letter', 'Closing Disclosure'],
  closing: ['Settlement Statement', 'Title Insurance', 'Commission Disbursement (CDA)'],
  broker_review: ['All Prior Documents', 'Broker Review Checklist'],
  completed: [],
};

const BrokerageDealBoard: React.FC = () => {
  const navigate = useNavigate();
  const [stages, setStages] = useState<Record<string, BrokerageDealCard[]>>({});
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Modal state
  const [selectedCard, setSelectedCard] = useState<BrokerageDealCard | null>(null);

  // ─── Fetch board ───
  const fetchBoard = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/broker/transactions/deal-board');
      if (res.data?.success) {
        setStages(res.data.stages || {});
        setCounts(res.data.counts || {});
      }
    } catch (err) {
      console.error('[BrokerageDealBoard] fetch error:', err);
      toast.error('Failed to load deal board');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  // ─── Drag & Drop (TC/Broker moves cards between stages) ───
  const handleDragEnd = useCallback(async (result: DropResult) => {
    if (!result.destination) return;
    const from = result.source.droppableId;
    const to = result.destination.droppableId;
    if (from === to) return;

    const cardId = result.draggableId;
    const card = stages[from]?.find(c => c.id === cardId);
    if (!card) return;

    // Optimistic update
    setStages(prev => {
      const next = { ...prev };
      next[from] = (next[from] || []).filter(c => c.id !== cardId);
      next[to] = [{ ...card, stage: to }, ...(next[to] || [])];
      return next;
    });
    setCounts(prev => ({
      ...prev,
      [from]: Math.max(0, (prev[from] || 0) - 1),
      [to]: (prev[to] || 0) + 1,
    }));

    try {
      await axiosInstance.patch(`/broker/transactions/${cardId}/stage`, { stage: to });
      const stageLabel = STAGES.find(s => s.key === to)?.label || to;
      toast.success(`Moved to ${stageLabel}`);
    } catch (err) {
      console.error('[BrokerageDealBoard] stage update error:', err);
      toast.error('Failed to update stage');
      fetchBoard(); // Revert
    }
  }, [stages, fetchBoard]);

  // ─── Approve (Broker signs off) ───
  const handleApprove = useCallback(async (transactionId: string) => {
    try {
      await axiosInstance.post(`/broker/transactions/${transactionId}/approve`);
      toast.success('Transaction approved and closed');
      fetchBoard();
    } catch (err) {
      console.error('[BrokerageDealBoard] approve error:', err);
      toast.error('Failed to approve transaction');
    }
  }, [fetchBoard]);

  // ─── Filter by search ───
  const filterCards = (cards: BrokerageDealCard[]) => {
    if (!search.trim()) return cards;
    const q = search.toLowerCase();
    return cards.filter(c =>
      c.clientName.toLowerCase().includes(q) ||
      c.agentName.toLowerCase().includes(q) ||
      c.propertyAddress.toLowerCase().includes(q) ||
      c.propertyCity.toLowerCase().includes(q)
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400, color: C.muted }}>
        <FontAwesomeIcon icon={faSpinnerIcon} spin size="2x" />
        <span style={{ marginLeft: 12, fontSize: 16 }}>Loading deal board...</span>
      </div>
    );
  }

  const totalDeals = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div style={{ padding: 0 }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 16, flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <h4 style={{ margin: 0, color: C.navy, fontWeight: 700 }}>
            Brokerage Deal Board
          </h4>
          <p style={{ margin: '4px 0 0', color: C.muted, fontSize: 13 }}>
            {totalDeals} active file{totalDeals !== 1 ? 's' : ''} in processing
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <FontAwesomeIcon icon={faSearch} style={{
              position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
              color: C.muted, fontSize: 13,
            }} />
            <input
              type="text" placeholder="Search client, agent, address..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={{
                padding: '8px 12px 8px 32px', border: `1px solid ${C.off}`, borderRadius: 6,
                fontSize: 13, width: 260, outline: 'none',
              }}
            />
          </div>
          <button
            onClick={() => { setLoading(true); fetchBoard(); }}
            style={{
              background: C.navy, color: C.white, border: 'none', borderRadius: 6,
              padding: '8px 14px', fontSize: 13, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <FontAwesomeIcon icon={faSyncAlt} /> Refresh
          </button>
        </div>
      </div>

      {/* Scrollbar styling for kanban */}
      <style>{`
        .brokerage-kanban-scroll::-webkit-scrollbar { height: 10px; }
        .brokerage-kanban-scroll::-webkit-scrollbar-track { background: ${C.off}; border-radius: 5px; }
        .brokerage-kanban-scroll::-webkit-scrollbar-thumb { background: ${C.gold}; border-radius: 5px; min-width: 60px; }
        .brokerage-kanban-scroll::-webkit-scrollbar-thumb:hover { background: ${C.gold2}; }
      `}</style>

      {/* Kanban */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div
          className="brokerage-kanban-scroll"
          style={{
            display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 16,
            minHeight: 500, scrollBehavior: 'smooth',
          }}
        >
          {STAGES.map(stageConfig => {
            const cards = filterCards(stages[stageConfig.key] || []);
            const count = counts[stageConfig.key] || 0;

            return (
              <div key={stageConfig.key} style={{
                minWidth: 280, maxWidth: 320, flex: '1 0 280px',
                background: '#f8f9fa', borderRadius: 8,
                display: 'flex', flexDirection: 'column',
              }}>
                {/* Stage header */}
                <div style={{
                  padding: '12px 14px', borderBottom: `3px solid ${stageConfig.color}`,
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <FontAwesomeIcon icon={stageConfig.icon} style={{ color: stageConfig.color, fontSize: 14 }} />
                  <span style={{ fontWeight: 700, fontSize: 13, color: C.navy, flex: 1 }}>
                    {stageConfig.label}
                  </span>
                  <span style={{
                    background: stageConfig.color, color: '#fff', borderRadius: '50%',
                    width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 700,
                  }}>{count}</span>
                </div>

                {/* Cards */}
                <Droppable droppableId={stageConfig.key}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{
                        padding: 8, flex: 1, minHeight: 100,
                        background: snapshot.isDraggingOver ? `${stageConfig.color}10` : 'transparent',
                        transition: 'background 0.2s',
                      }}
                    >
                      {cards.map((card, idx) => (
                        <Draggable key={card.id} draggableId={card.id} index={idx}>
                          {(dragProvided, dragSnapshot) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              style={{
                                ...dragProvided.draggableProps.style,
                                background: '#fff',
                                borderRadius: 8,
                                padding: 12,
                                marginBottom: 8,
                                border: `1px solid ${dragSnapshot.isDragging ? stageConfig.color : '#e8eef6'}`,
                                boxShadow: dragSnapshot.isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.06)',
                                cursor: dragSnapshot.isDragging ? 'grabbing' : 'pointer',
                              }}
                              onClick={() => !dragSnapshot.isDragging && setSelectedCard(card)}
                            >
                              {/* Drag handle + badges */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                                <span {...dragProvided.dragHandleProps} style={{ color: C.muted, cursor: 'grab' }}>
                                  <FontAwesomeIcon icon={faGripVertical} style={{ fontSize: 11 }} />
                                </span>
                                <TypeBadge type={card.type} />
                                <ComplianceBadge status={card.complianceStatus} />
                                {card.daysInStage > 7 && (
                                  <span style={{
                                    background: '#e74c3c20', color: '#e74c3c', padding: '2px 6px',
                                    borderRadius: 4, fontSize: 10, fontWeight: 600,
                                  }}>
                                    <FontAwesomeIcon icon={faClock} /> {card.daysInStage}d
                                  </span>
                                )}
                              </div>

                              {/* Client + property */}
                              <div style={{ fontWeight: 700, fontSize: 14, color: C.navy, marginBottom: 4 }}>
                                {card.clientName}
                              </div>
                              {card.propertyAddress && card.propertyAddress !== 'Address TBD' && (
                                <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>
                                  <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: C.gold, marginRight: 4 }} />
                                  {card.propertyAddress}{card.propertyCity ? `, ${card.propertyCity}` : ''}
                                </div>
                              )}
                              {card.propertyPrice && (
                                <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>
                                  <FontAwesomeIcon icon={faDollarSign} style={{ color: C.green, marginRight: 4 }} />
                                  ${card.propertyPrice.toLocaleString()}
                                </div>
                              )}

                              {/* Agent */}
                              <div style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '6px 0', borderTop: `1px solid ${C.off}`,
                                marginTop: 4,
                              }}>
                                <FontAwesomeIcon icon={faUser} style={{ color: C.muted, fontSize: 11 }} />
                                <span style={{ fontSize: 12, color: C.navy, fontWeight: 600 }}>
                                  {card.agentName}
                                </span>
                              </div>

                              {/* Doc count + actions */}
                              <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                marginTop: 6,
                              }}>
                                <span style={{ fontSize: 11, color: C.muted }}>
                                  <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: 4 }} />
                                  {card.docCount} doc{card.docCount !== 1 ? 's' : ''}
                                </span>

                                {/* Approve button — only show in closing stage */}
                                {card.stage === 'closing' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (window.confirm(`Approve and close: ${card.clientName}?`)) {
                                        handleApprove(card.id);
                                      }
                                    }}
                                    style={{
                                      background: C.green, color: '#fff', border: 'none',
                                      borderRadius: 4, padding: '4px 10px', fontSize: 11,
                                      fontWeight: 700, cursor: 'pointer',
                                      display: 'flex', alignItems: 'center', gap: 4,
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faCheck} /> Approve
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}

                      {cards.length === 0 && (
                        <div style={{
                          textAlign: 'center', padding: '24px 12px', color: C.muted, fontSize: 12,
                        }}>
                          No files in this stage
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
      </DragDropContext>

      {/* ── Deal Detail Modal ── */}
      {selectedCard && (
        <DealDetailModal
          card={selectedCard}
          stages={STAGES}
          onClose={() => setSelectedCard(null)}
          onStageChange={async (newStage: string) => {
            try {
              await axiosInstance.patch(`/broker/transactions/${selectedCard.id}/stage`, { stage: newStage });
              const label = STAGES.find(s => s.key === newStage)?.label || newStage;
              toast.success(`Moved to ${label}`);
              setSelectedCard(prev => prev ? { ...prev, stage: newStage } : null);
              fetchBoard();
            } catch {
              toast.error('Failed to update stage');
            }
          }}
          onApprove={async () => {
            try {
              await axiosInstance.post(`/broker/transactions/${selectedCard.id}/approve`);
              toast.success('Transaction approved and closed');
              setSelectedCard(null);
              fetchBoard();
            } catch {
              toast.error('Failed to approve');
            }
          }}
          onOpenFull={() => {
            navigate(`/broker-portal/transactions/${selectedCard.id}`);
            setSelectedCard(null);
          }}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Deal Detail Modal — Quick review popup
// ---------------------------------------------------------------------------
const DealDetailModal: React.FC<{
  card: BrokerageDealCard;
  stages: StageConfig[];
  onClose: () => void;
  onStageChange: (stage: string) => void;
  onApprove: () => void;
  onOpenFull: () => void;
}> = ({ card, stages, onClose, onStageChange, onApprove, onOpenFull }) => {
  const currentStage = stages.find(s => s.key === card.stage);
  const currentIdx = stages.findIndex(s => s.key === card.stage);
  const nextStage = currentIdx < stages.length - 1 ? stages[currentIdx + 1] : null;
  const isListing = card.type === 'listing';

  // Fetch real document data + AI brief on mount
  const [docs, setDocs] = useState<any[]>([]);
  const [briefItems, setBriefItems] = useState<any[]>([]);
  const [briefScore, setBriefScore] = useState<number | null>(null);
  const [loadingDocs, setLoadingDocs] = useState(true);

  useEffect(() => {
    // Fetch uploaded documents
    axiosInstance.get(`/broker-review/documents/${card.id}`)
      .then(({ data }) => {
        if (data.success) setDocs(data.documents || []);
      })
      .catch(() => {})
      .finally(() => setLoadingDocs(false));

    // Fetch AI brief items from deal health
    axiosInstance.get(`/deal-coach/${card.id}/brief`)
      .then(({ data }) => {
        if (data.success) {
          setBriefScore(data.score ?? null);
          // Parse NEEDS ATTENTION from brief
          const brief = data.brief || '';
          const match = brief.match(/#{0,3}\s*NEEDS ATTENTION[^\n]*\n([\s\S]*?)(?=\n#{1,3}\s*(?:UPCOMING|TIP|COMPLETED)|💡|$)/i);
          if (match) {
            const items = match[1].split('\n')
              .map((l: string) => l.replace(/^\d+\.\s*/, '').replace(/\*\*/g, '').replace(/^[•\-]\s*/, '').trim())
              .filter((l: string) => l.length > 15);
            setBriefItems(items);
          }
        }
      })
      .catch(() => {});
  }, [card.id]);

  const uploadedCount = docs.filter(d => d.broker_review_status !== 'exempt').length;
  const approvedCount = docs.filter(d => d.broker_review_status === 'approved').length;
  const pendingCount = docs.filter(d => d.broker_review_status === 'pending_review' || !d.broker_review_status).length;

  // Transaction-aware next step label
  const getNextAction = () => {
    if (card.stage === 'broker_review') return null; // Approve button handles this
    if (card.stage === 'completed') return null;
    if (isListing) {
      if (card.stage === 'listing' || card.stage === 'pre_contract') return { label: 'Move to Showing', stage: 'showing' };
      if (card.stage === 'showing') return { label: 'Move to Under Contract', stage: 'under_contract' };
      return nextStage ? { label: `Move to ${nextStage.label}`, stage: nextStage.key } : null;
    }
    return nextStage ? { label: `Move to ${nextStage.label}`, stage: nextStage.key } : null;
  };
  const nextAction = getNextAction();

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: 16, width: 640, maxHeight: '90vh',
          boxShadow: '0 24px 80px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          background: C.navy, padding: '20px 24px',
          borderBottom: `3px solid ${currentStage?.color || C.gold}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%',
                background: currentStage?.color || C.gold,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 700, fontSize: 16,
              }}>
                <FontAwesomeIcon icon={currentStage?.icon || faFolderOpen} />
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, color: '#fff', fontWeight: 700 }}>
                  {card.clientName}
                </h2>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <TypeBadge type={card.type} />
                  <ComplianceBadge status={card.complianceStatus} />
                  <span style={{
                    fontSize: 10, padding: '2px 8px', borderRadius: 4,
                    background: `${currentStage?.color}33`, color: currentStage?.color,
                    fontWeight: 600,
                  }}>
                    {currentStage?.label || card.stage}
                  </span>
                  {briefScore !== null && (
                    <span style={{
                      fontSize: 10, padding: '2px 8px', borderRadius: 4,
                      background: briefScore >= 80 ? '#27ae6033' : briefScore >= 50 ? '#f39c1233' : '#e74c3c33',
                      color: briefScore >= 80 ? '#27ae60' : briefScore >= 50 ? '#f39c12' : '#e74c3c',
                      fontWeight: 700,
                    }}>
                      {briefScore}%
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button onClick={onClose} style={{
              background: 'transparent', border: 'none', color: C.muted, fontSize: 18, cursor: 'pointer',
            }}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
          {/* Deal Info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div>
              <p style={{ margin: 0, fontSize: 11, color: C.muted, textTransform: 'uppercase', fontWeight: 600 }}>Property</p>
              <p style={{ margin: '4px 0 0', fontSize: 14, color: C.navy, fontWeight: 600 }}>
                {card.propertyAddress !== 'Address TBD' ? card.propertyAddress : 'No address yet'}
                {card.propertyCity ? `, ${card.propertyCity}` : ''}
              </p>
              {card.propertyPrice && (
                <p style={{ margin: '2px 0 0', fontSize: 16, color: C.gold, fontWeight: 700 }}>
                  ${card.propertyPrice.toLocaleString()}
                </p>
              )}
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 11, color: C.muted, textTransform: 'uppercase', fontWeight: 600 }}>Agent</p>
              <p style={{ margin: '4px 0 0', fontSize: 14, color: C.navy, fontWeight: 600 }}>{card.agentName}</p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: C.muted }}>{card.agentEmail}</p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 11, color: C.muted, textTransform: 'uppercase', fontWeight: 600 }}>Days in Stage</p>
              <p style={{ margin: '4px 0 0', fontSize: 14, color: card.daysInStage > 7 ? C.red : C.navy, fontWeight: 600 }}>
                {card.daysInStage} day{card.daysInStage !== 1 ? 's' : ''}
              </p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 11, color: C.muted, textTransform: 'uppercase', fontWeight: 600 }}>Documents</p>
              <p style={{ margin: '4px 0 0', fontSize: 14, color: C.navy, fontWeight: 600 }}>
                {approvedCount} approved · {pendingCount} pending
              </p>
            </div>
          </div>

          {/* AI Recommendations */}
          {briefItems.length > 0 && (
            <div style={{
              background: '#fef2f2', borderRadius: 8, padding: 14, marginBottom: 16,
              border: '1px solid #fecaca', borderLeft: `4px solid ${C.red}`,
            }}>
              <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 700, color: C.red, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 6 }} />
                AI Recommendations
              </p>
              {briefItems.slice(0, 4).map((item, i) => (
                <div key={i} style={{ fontSize: 12, color: '#555', padding: '4px 0', lineHeight: 1.5 }}>
                  {i + 1}. {item}
                </div>
              ))}
            </div>
          )}

          {/* Uploaded Documents — real data */}
          <div style={{
            background: '#f8f9fb', borderRadius: 8, padding: 16, marginBottom: 16,
            border: '1px solid #e8eef6',
          }}>
            <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: C.navy }}>
              <FontAwesomeIcon icon={faClipboardCheck} style={{ marginRight: 6, color: C.gold }} />
              Documents ({uploadedCount} uploaded)
            </p>
            {loadingDocs ? (
              <p style={{ fontSize: 12, color: C.muted }}>Loading...</p>
            ) : docs.length === 0 ? (
              <p style={{ fontSize: 12, color: C.muted }}>No documents uploaded yet</p>
            ) : (
              docs.filter(d => d.broker_review_status !== 'exempt').map((doc, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
                  borderBottom: i < docs.length - 1 ? '1px solid #e8eef6' : 'none',
                }}>
                  <div style={{
                    width: 18, height: 18, borderRadius: 4,
                    border: `2px solid ${doc.broker_review_status === 'approved' ? C.green : C.orange}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: doc.broker_review_status === 'approved' ? C.green : 'transparent',
                  }}>
                    {doc.broker_review_status === 'approved' && (
                      <FontAwesomeIcon icon={faCheck} style={{ color: '#fff', fontSize: 10 }} />
                    )}
                  </div>
                  <span style={{ fontSize: 12, color: C.navy, flex: 1 }}>
                    {doc.original_filename || doc.filename}
                  </span>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3,
                    background: doc.broker_review_status === 'approved' ? '#27ae6020' : '#f39c1220',
                    color: doc.broker_review_status === 'approved' ? '#27ae60' : '#f39c12',
                  }}>
                    {doc.broker_review_status === 'approved' ? 'Approved' : 'Pending'}
                  </span>
                </div>
              ))
            )}
          </div>

          {/* Notes */}
          {card.notes && (
            <div style={{ background: '#fffdf5', borderRadius: 8, padding: 12, marginBottom: 20, border: '1px solid #f0e6c0' }}>
              <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: C.navy }}>Notes</p>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#555' }}>{card.notes}</p>
            </div>
          )}
        </div>

        {/* Footer Actions — transaction-aware */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid #e8eef6',
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        }}>
          {/* Transaction-aware next step */}
          {nextAction && card.stage !== 'broker_review' && card.stage !== 'completed' && (
            <button
              onClick={() => onStageChange(nextAction.stage)}
              style={{
                background: stages.find(s => s.key === nextAction.stage)?.color || C.gold,
                color: '#fff', border: 'none', borderRadius: 8,
                padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <FontAwesomeIcon icon={faArrowRight} /> {nextAction.label}
            </button>
          )}

          {/* Ready for Broker Review */}
          {card.stage !== 'broker_review' && card.stage !== 'completed' && (
            <button
              onClick={() => onStageChange('broker_review')}
              style={{
                background: C.navy, color: C.gold, border: `1px solid ${C.gold}`,
                borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <FontAwesomeIcon icon={faPaperPlane} /> Ready for Broker Review
            </button>
          )}

          {/* Broker Sign-off — review and approve documents */}
          {card.stage === 'broker_review' && (
            <>
              <button
                onClick={onOpenFull}
                style={{
                  background: C.gold, color: C.navy, border: 'none', borderRadius: 8,
                  padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <FontAwesomeIcon icon={faClipboardCheck} /> Review & Sign Documents
              </button>
              <button
                onClick={() => {
                  if (window.confirm(`Approve and close: ${card.clientName}?`)) onApprove();
                }}
                style={{
                  background: C.green, color: '#fff', border: 'none', borderRadius: 8,
                  padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <FontAwesomeIcon icon={faCheck} /> Approve & Close
              </button>
            </>
          )}

          {/* Open full detail */}
          <button
            onClick={onOpenFull}
            style={{
              background: 'transparent', color: C.navy, border: `1px solid #e8eef6`,
              borderRadius: 8, padding: '10px 16px', fontSize: 13, cursor: 'pointer',
              marginLeft: 'auto',
            }}
          >
            Open Full Detail
          </button>
        </div>
      </div>
    </div>
  );
};

export default BrokerageDealBoard;
